import { Theme } from './Theme'
import { GameSettings } from './Settings'

/**
 * Global application state shared across the rendering and interaction layers.
 *
 * Holds layout dimensions (recalculated on resize), the active theme,
 * user settings, and the current screen (playing, menu, or completed).
 * Unlike `CardsState`, this is a singleton — not stored in a history stack.
 */
export interface AppState {
    /** Number of tableau columns (always 7 in standard Klondike). */
    NUM_TABLEAU: number
    /** Number of stock piles (always 1). */
    NUM_STOCK: number
    /** Number of waste piles (always 1). */
    NUM_WASTE: number
    /** Number of foundation piles (always 4). */
    NUM_FOUNDATIONS: number
    /** Cards drawn per stock tap (1 or 3, derived from `settings.drawCount`). */
    NUM_CARDS_FLIPPED: number
    /** Rendered card width in CSS pixels. Undefined until first layout. */
    cardWidth: number | undefined
    /** Rendered card height in CSS pixels. Undefined until first layout. */
    cardHeight: number | undefined
    /** Viewport height in CSS pixels. */
    windowHeight: number | undefined
    /** Viewport width in CSS pixels. */
    windowWidth: number | undefined
    /** Horizontal gap between cards in CSS pixels. */
    cardSpacing: number | undefined
    /** Vertical offset between stacked tableau cards in CSS pixels. */
    cardYOffset: number | undefined
    /** Horizontal padding before the first foundation pile in CSS pixels. */
    foundationXOffset: number | undefined
    /** The currently active visual theme (colors, card assets). */
    currentTheme: Theme
    /** Which screen the app is currently showing. */
    currentAppState: CurrentAppState
    /** User-configurable game settings (persisted to localStorage). */
    settings: GameSettings
    /** True when running as PWA, standalone, or inside Cordova — hides fullscreen button. */
    isStandalone: boolean
    /** Completion-screen toggle: show/hide records panel over win view. */
    showWinRecords: boolean
    /** Menu-screen toggle: show/hide records leaderboard over settings. */
    showMenuRecords: boolean
}

/** Top-level screen states. */
export enum CurrentAppState {
    /** The main game board — cards are interactive. */
    Playing,
    /** Settings/theme menu overlay is open. */
    Menu,
    /** Win animation screen after all foundations are filled. */
    Completed,
}
